import type { RequisitionStatus } from '#shared/types';
import type { BadgeTone } from '#client/components/ui/Badge';
import type { Permission } from '#shared/types';

/**
 * What the detail screen offers, from each state.
 *
 * This mirrors `server/procurement/requisition-ladder.ts`, and mirroring is all
 * it does — **hiding a button is not authorisation**. Every action here is
 * refused server-side by the same table if it is reached another way; this
 * exists so the screen does not offer people moves that will 409 or 403.
 *
 * Shared between the list and the detail screens rather than declared in the
 * one that happens to need it first.
 */

/**
 * Tone by *what to do about it*, not by position in the ladder.
 *
 * `submitted` is amber because it is waiting on somebody; `approved` and
 * `ordered` are informational because the next move is the supplier's.
 * Colouring all eight states differently would make the list a swatch.
 */
export const TONES: Record<RequisitionStatus, BadgeTone> = {
  draft: 'draft',
  submitted: 'warning',
  approved: 'info',
  ordered: 'info',
  partially_received: 'amended',
  received: 'success',
  rejected: 'critical',
  cancelled: 'neutral',
};

/**
 * A draft is the only state that can be edited or deleted. Changing what was
 * asked for after approval rewrites the thing somebody approved; deleting
 * anything past draft would erase an approval, an order placed with a supplier,
 * or a delivery. The server refuses both.
 */
export function isRequisitionDraft(status: RequisitionStatus): boolean {
  return status === 'draft';
}

export interface TransitionAction {
  to: RequisitionStatus;
  label: string;
  variant: 'primary' | 'secondary' | 'danger';
  /** The permission it needs — the server checks the same one per transition. */
  capability: Permission;
}

const ACTIONS: Record<RequisitionStatus, readonly TransitionAction[]> = {
  draft: [
    {
      to: 'submitted',
      label: 'Send for approval',
      variant: 'primary',
      capability: 'requisition.submit',
    },
    { to: 'cancelled', label: 'Cancel', variant: 'secondary', capability: 'requisition.cancel' },
  ],
  submitted: [
    { to: 'approved', label: 'Approve', variant: 'primary', capability: 'requisition.approve' },
    { to: 'rejected', label: 'Reject', variant: 'danger', capability: 'requisition.reject' },
    { to: 'cancelled', label: 'Cancel', variant: 'secondary', capability: 'requisition.cancel' },
  ],
  approved: [
    {
      to: 'ordered',
      label: 'Mark as ordered',
      variant: 'primary',
      capability: 'requisition.order',
    },
    { to: 'cancelled', label: 'Cancel', variant: 'secondary', capability: 'requisition.cancel' },
  ],
  ordered: [
    { to: 'cancelled', label: 'Cancel', variant: 'secondary', capability: 'requisition.cancel' },
  ],
  partially_received: [
    // A supplier who cannot supply the rest is the ordinary case. The stock
    // already received stays received either way.
    {
      to: 'cancelled',
      label: 'Close as cancelled',
      variant: 'secondary',
      capability: 'requisition.cancel',
    },
  ],
  received: [],
  rejected: [],
  cancelled: [],
};

export function transitionsFrom(
  status: RequisitionStatus,
  can: (capability: Permission) => boolean,
): TransitionAction[] {
  return ACTIONS[status].filter((action) => can(action.capability));
}
